import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import {
	invalidateMeetingPrepTemplates,
	selectBoundMeetingPrepTemplate,
	useCreateMeetingPrepTemplateMutation,
	useMeetingPrepTemplatesQuery,
	useUpdateMeetingPrepTemplateMutation,
} from '../../../../../queries';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { NavLink } from 'react-router-dom';
import { Button } from '../../../../components-v2/Button';
import { Checkbox } from '../../../../components-v2/Checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components-v2/Select';
import { TextArea } from '../../../../components-v2/TextArea';
import { ROUTES } from '../../../../constants/routes';
import { RotateCCWClockIcon } from '../../../svgs/icons/RotateCCWClockIcon';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { styleSheet } from './styles';
import {
	coerceSendHourForTiming,
	DEFAULT_SEND_HOUR,
	formatLastDeliveredLabel,
	getDefaultSendHour,
	getSendHourOptions,
} from './timeUtils';

interface IProps {
	onCancel?: () => void;
}

interface FormState {
	enabled: boolean;
	sendEmail: boolean;
	sendHourLocal: number;
	deliveryTiming: Api.MeetingPrepDeliveryTiming;
	weekendDelivery: boolean;
	includeInternalAttendees: boolean;
	defaultPrompt: string | null;
}

/** The stable label the server's own synced rows carry, so migrated and UI-created rows match. */
const MEETING_PREP_TEMPLATE_NAME = 'Meeting Prep';

const getDefaultFormState = (template?: Api.MeetingPrepTemplate): FormState => {
	const deliveryTiming = template?.deliveryTiming ?? Api.MeetingPrepDeliveryTiming.MorningOf;

	return {
		enabled: template?.scheduleEnabled ?? false,
		sendEmail: template?.sendEmail ?? true,
		// Coerced so an hour the mode doesn't offer can't leave the picker showing its placeholder.
		sendHourLocal: coerceSendHourForTiming(deliveryTiming, template?.sendHourLocal ?? DEFAULT_SEND_HOUR),
		deliveryTiming,
		weekendDelivery: template?.weekendDelivery ?? false,
		includeInternalAttendees: template?.includeInternalAttendees ?? false,
		defaultPrompt: template?.objectivePrompt ?? null,
	};
};

/** Shared so the create and update bodies can't drift apart. */
const toTemplateFields = (form: FormState) => ({
	scheduleEnabled: form.enabled,
	sendEmail: form.sendEmail,
	sendHourLocal: form.sendHourLocal,
	deliveryTiming: form.deliveryTiming,
	weekendDelivery: form.weekendDelivery,
	includeInternalAttendees: form.includeInternalAttendees,
	// Empty rather than undefined: the server stores a blank prompt as absent, while an undefined key is
	// dropped by JSON.stringify and so says nothing about clearing it.
	objectivePrompt: form.defaultPrompt ?? '',
});

const DELIVERY_TIMING_OPTIONS = [
	{ value: Api.MeetingPrepDeliveryTiming.MorningOf, label: 'Morning of the meeting day' },
	{ value: Api.MeetingPrepDeliveryTiming.EveningBefore, label: 'Evening before' },
];

const DELIVERY_TIMING_ITEMS = DELIVERY_TIMING_OPTIONS.map(option => ({
	label: option.label,
	value: String(option.value),
}));

/**
 * Per-user Meeting Prep preferences form. Rendered inside a SettingsGroup that is only mounted when
 * the account has Meeting Prep enabled (see PersonalSettings/index.tsx). An empty template list means
 * never configured, so the first save creates and later saves update.
 */
export const MeetingPrepSettings: React.FC<IProps> = ({ onCancel }) => {
	const errorMessages = useErrorMessages();
	const { logEvent, logApiError } = useEventLogging('MeetingPrepSettings');

	const templatesQuery = useMeetingPrepTemplatesQuery({
		meta: { toastErrorMessage: 'Failed to load your Meeting Prep settings.' },
	});

	const loadedTemplate = templatesQuery.data ? selectBoundMeetingPrepTemplate(templatesQuery.data) : undefined;

	const [form, setForm] = React.useState<FormState>(getDefaultFormState());

	// Seed the form once, when the templates first load. Reseeding on every `data` change would let a
	// background refetch (window focus/reconnect) clobber in-progress edits.
	const hasSeededRef = React.useRef(false);
	React.useEffect(() => {
		if (templatesQuery.data && !hasSeededRef.current) {
			hasSeededRef.current = true;
			setForm(getDefaultFormState(loadedTemplate));
		}
	}, [templatesQuery.data, loadedTemplate]);

	const onSaved = (template: Api.MeetingPrepTemplate) => {
		// Log what was actually persisted (the server response), not the latest form closure.
		logEvent('UpdateMeetingPrepSettings', {
			enabled: template.scheduleEnabled,
			deliveryTiming: template.deliveryTiming,
		});
		// Briefs only surface later via email, so confirm the save explicitly.
		toast.success('Meeting Prep settings saved.');
	};

	const hadCreateConflictRef = React.useRef(false);

	const createMutation = useCreateMeetingPrepTemplateMutation({
		onSuccess: onSaved,
		onError: error => {
			// A create raced another writer and lost the server's one-per-user index. Refetching supplies that
			// template's id, so the next save updates. The form keeps the user's edits on purpose — reseeding
			// would discard them, and auto-retrying as an update would overwrite the other writer unseen.
			if (error.systemCode === 409) {
				hadCreateConflictRef.current = true;
				invalidateMeetingPrepTemplates();
				toast.info('Your Meeting Prep settings already exist. Save again to apply your changes.');
				return;
			}
			logApiError('CreateMeetingPrepTemplate-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const updateMutation = useUpdateMeetingPrepTemplateMutation({
		onSuccess: onSaved,
		onError: error => {
			logApiError('UpdateMeetingPrepTemplate-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	// A 409 means the template exists, but its id only arrives with the refetch. Saving in that window would
	// fire a second create and conflict again, so hold the buttons until the id lands. Scoped to a conflict
	// rather than any in-flight fetch, so a routine focus/reconnect refetch leaves Save alone.
	const isResolvingConflict = hadCreateConflictRef.current && !loadedTemplate?.id;
	const isSaving = createMutation.isPending || updateMutation.isPending || isResolvingConflict;

	const updateField = <TKey extends keyof FormState>(key: TKey, value: FormState[TKey]) => {
		setForm(prev => ({ ...prev, [key]: value }));
	};

	// The master toggle drives both enabled and sendEmail — in v1 the only delivery channel is email. On
	// enable, restore the persisted sendEmail intent rather than hardcoding true, so a server-side
	// sendEmail=false isn't silently flipped by an off→on cycle within the session.
	const onToggleEnabled = (checked: boolean) => {
		setForm(prev => ({
			...prev,
			enabled: checked,
			sendEmail: checked ? (loadedTemplate?.sendEmail ?? true) : prev.sendEmail,
		}));
	};

	// Switching modes has to move the send hour too, since the two are validated together server-side.
	// Restore the user's persisted hour when they switch back to a mode they'd saved.
	const onChangeDeliveryTiming = (deliveryTiming: Api.MeetingPrepDeliveryTiming) => {
		setForm(prev => {
			if (prev.deliveryTiming === deliveryTiming) {
				return prev;
			}

			// Absent deliveryTiming means MorningOf, the same default the form seed applies. Without normalizing
			// it, a record written before the field existed would fail this match and lose the morning hour the
			// user had actually saved. Coerced on the way out for the same reason the seed coerces.
			const persistedTiming = loadedTemplate?.deliveryTiming ?? Api.MeetingPrepDeliveryTiming.MorningOf;
			const sendHourLocal =
				loadedTemplate && persistedTiming === deliveryTiming
					? coerceSendHourForTiming(deliveryTiming, loadedTemplate.sendHourLocal ?? DEFAULT_SEND_HOUR)
					: getDefaultSendHour(deliveryTiming);

			return { ...prev, deliveryTiming, sendHourLocal };
		});
	};

	const onSave = () => {
		if (loadedTemplate?.id) {
			// Spread the loaded template so fields this form doesn't edit survive the full-object replace.
			updateMutation.mutate({ ...loadedTemplate, ...toTemplateFields(form) });
			return;
		}

		createMutation.mutate({
			name: MEETING_PREP_TEMPLATE_NAME,
			isActive: true,
			...toTemplateFields(form),
		});
	};

	const onCancelClicked = () => {
		setForm(getDefaultFormState(loadedTemplate));
		onCancel?.();
	};

	if (templatesQuery.isLoading) {
		return <LoadingSpinner type='small' />;
	}

	// Don't fall through to a defaults-seeded form on load failure — saving from it would create a second
	// template or overwrite the user's real settings with hardcoded defaults.
	if (templatesQuery.isError) {
		return (
			<div className={css(styleSheet.root)}>
				<span className={css(styleSheet.statusLine)}>
					We couldn’t load your Meeting Prep settings. Please refresh the page and try again.
				</span>
			</div>
		);
	}

	const lastRunDate = loadedTemplate?.lastRunDate;
	const isEveningBefore = form.deliveryTiming === Api.MeetingPrepDeliveryTiming.EveningBefore;
	const sendHourOptions = getSendHourOptions(form.deliveryTiming);
	// value↔label map for the Select root so the trigger shows the formatted label even while the
	// dropdown is closed (Base UI's Select.Value can't resolve a label from unmounted items otherwise).
	const sendHourItems = sendHourOptions.map(option => ({ label: option.label, value: String(option.value) }));

	return (
		<div className={css(styleSheet.root)}>
			<div className={css(styleSheet.header)}>
				<Button variant='link' asChild>
					<NavLink to={ROUTES.meetingPrep.briefs}>
						<RotateCCWClockIcon />
						View your briefs
					</NavLink>
				</Button>
			</div>
			<div className={css(styleSheet.row)}>
				<Checkbox
					checked={form.enabled}
					id='meeting-prep-enabled'
					onCheckedChange={value => onToggleEnabled(value === true)}
				/>
				<label className={css(styleSheet.label)} htmlFor='meeting-prep-enabled'>
					Email me a prep brief
				</label>
			</div>

			{form.enabled ? (
				<>
					<div className={css(styleSheet.field)}>
						<span className={css(styleSheet.label)}>Deliver</span>
						<Select
							items={DELIVERY_TIMING_ITEMS}
							value={String(form.deliveryTiming)}
							onValueChange={value => onChangeDeliveryTiming(value as Api.MeetingPrepDeliveryTiming)}
						>
							<SelectTrigger styles={styleSheet.deliveryTrigger}>
								<SelectValue placeholder='Select when to deliver' />
							</SelectTrigger>
							<SelectContent>
								{DELIVERY_TIMING_OPTIONS.map(option => (
									<SelectItem key={option.value} value={String(option.value)}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<span className={css(styleSheet.helpText)}>
							{isEveningBefore
								? 'Your brief arrives the evening before and covers the next day’s meetings.'
								: 'Your brief arrives on the morning of the meetings it covers.'}
						</span>
					</div>

					<div className={css(styleSheet.field)}>
						<span className={css(styleSheet.label)}>Send time</span>
						<Select
							items={sendHourItems}
							value={String(form.sendHourLocal)}
							onValueChange={value => updateField('sendHourLocal', Number(value))}
						>
							<SelectTrigger styles={styleSheet.timeTrigger}>
								<SelectValue placeholder='Select a time' />
							</SelectTrigger>
							<SelectContent>
								{sendHourOptions.map(option => (
									<SelectItem key={option.value} value={String(option.value)}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className={css(styleSheet.field)}>
						<div className={css(styleSheet.row)}>
							<Checkbox
								checked={form.weekendDelivery}
								id='meeting-prep-weekend'
								onCheckedChange={value => updateField('weekendDelivery', value === true)}
							/>
							<label className={css(styleSheet.label)} htmlFor='meeting-prep-weekend'>
								Deliver on weekends
							</label>
						</div>
						{isEveningBefore ? (
							<span className={css(styleSheet.helpText)}>
								Off means you’ll get a brief each weekday. Monday’s arrives Sunday evening.
							</span>
						) : null}
					</div>

					<div className={css(styleSheet.field)}>
						<div className={css(styleSheet.row)}>
							<Checkbox
								checked={form.includeInternalAttendees}
								id='meeting-prep-internal'
								onCheckedChange={value => updateField('includeInternalAttendees', value === true)}
							/>
							<label className={css(styleSheet.label)} htmlFor='meeting-prep-internal'>
								Include internal attendees
							</label>
						</div>
						<span className={css(styleSheet.helpText)}>
							When on, meetings with only your colleagues get prepped too.
						</span>
					</div>

					<div className={css(styleSheet.field)}>
						<label className={css(styleSheet.label)} htmlFor='meeting-prep-prompt'>
							Default focus / tone
						</label>
						<TextArea
							id='meeting-prep-prompt'
							placeholder='Optional guidance applied to every brief (e.g. “Focus on renewals”).'
							value={form.defaultPrompt ?? ''}
							onChange={event => updateField('defaultPrompt', event.target.value || null)}
						/>
					</div>

					{lastRunDate ? (
						<span className={css(styleSheet.statusLine)}>
							{/* Persisted timing, not the form's: this line describes the send that already happened.
							    Records written before the field existed carry no deliveryTiming, so default it. */}
							{formatLastDeliveredLabel(
								lastRunDate,
								loadedTemplate?.deliveryTiming ?? Api.MeetingPrepDeliveryTiming.MorningOf
							)}
						</span>
					) : null}
				</>
			) : null}

			<div className={css(styleSheet.footer)}>
				<Button onClick={onSave} isLoading={isSaving}>
					Save
				</Button>
				<Button variant='outline' onClick={onCancelClicked} disabled={isSaving}>
					Cancel
				</Button>
			</div>
		</div>
	);
};
